/**
 * ProgressionChip — per-record deal-progression chip on the analysis surface
 * (Phase C, C-4).
 *
 * Fully presentational, mirroring StageBadge's contract: the caller passes
 * the already-computed semantic values straight from the record (unlike
 * StageBadge, there are no raw CRM ids to resolve here — `crmStageProgression`
 * is itself the display value). Render gate is `evaluatedAt` — the
 * outcome-sync cron (Phase C, C-2/C-3) only sets it once progression has been
 * evaluated, so a null `evaluatedAt` means "not evaluated yet" (including the
 * dark-shipped default while the `stageAwareScoring` flag is off for
 * everyone) and the chip stays hidden rather than showing a stale/empty state.
 *
 * The gate deliberately checks BOTH `evaluatedAt` and `progression`: the cron
 * stamps `evaluatedAt` with a null `progression` when a record ages past the
 * 90-day unresolvable guard (see RecordMetadataBaseRepository.updateStageProgression),
 * so gating on `evaluatedAt` alone would crash on `PROGRESSION_CONFIG[null]`.
 * Do not "simplify" to a single check.
 */

import { useTranslation } from 'react-i18next';
import { TrendingUp, ArrowRight, TrendingDown } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import type { CrmStageProgression } from '@aica/api-types';

export interface ProgressionChipProps {
  /** Absent/null (together with `evaluatedAt`) => the chip is hidden entirely. */
  progression?: CrmStageProgression | null;
  /** Render gate: the chip only shows once the outcome-sync cron has evaluated progression. */
  evaluatedAt?: string | null;
  className?: string;
}

const PROGRESSION_CONFIG: Record<
  CrmStageProgression,
  { variant: BadgeProps['variant']; icon: typeof TrendingUp }
> = {
  advanced: { variant: 'success', icon: TrendingUp },
  held: { variant: 'default', icon: ArrowRight },
  regressed: { variant: 'danger', icon: TrendingDown },
};

export function ProgressionChip({
  progression,
  evaluatedAt,
  className,
}: ProgressionChipProps) {
  const { t } = useTranslation('records');

  if (!evaluatedAt || !progression) return null;

  const { variant, icon: Icon } = PROGRESSION_CONFIG[progression];

  return (
    <Badge
      variant={variant}
      size="sm"
      icon={<Icon className="h-3 w-3" aria-hidden="true" />}
      data-testid="progression-chip"
      className={className}
    >
      {t(`records.analysisPanel.stageProgression.${progression}`)}
    </Badge>
  );
}
